import PropTypes from 'prop-types';
import { Trip, TripCampaign } from '@eti/types';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import isEmpty from 'lodash/isEmpty';
import { constants } from '@eti/utils';
import { getTravelerPrices } from '../../../../utils/travelers';
import { addLegendNumberToSegmentEvents } from '../../../../widgets/trip-details/utils/format';
import { VALID_WITH_VOUCHER } from '../../../../constants/tripCharacteristics';
import {
  getCFARTicketConfig,
  getFlexibleTicketConfig,
  getTripTagsTitle,
  isCFARTicketAllowedForCarriers,
  isFlexibleTicketAllowedForCarriers,
} from '../../utils/resultUtils';
import ResultTrip from './ResultTrip';

const FlightResult = ({
  addTrip,
  index,
  isMultiStop,
  isSelectTripLoading,
  onShowAncillaryBundleModal,
  redirectToDBF,
  redirectToTravelerDetails,
  trip: {
    id: flightId,
    tripId,
    availableExtraProducts,
    bounds,
    includedCheckedBaggage,
    isVI,
    paymentMethodPrices,
    selectionKey,
    shareableUrl,
    systems,
    travelerPrices,
    travelerPricesWithoutPaymentDiscounts,
    tripCharacteristics,
    tripTags,
    tripTravelers,
    voucherAmount,
  },
  tripCampaign,
}) => {
  const { language } = useSiteContext();
  const { t } = useTranslation();
  const { p } = useProperty();
  const { SEARCH_RESULT_FLEX_BEHAVIOR_OPTIONS } = constants;
  const travelerPriceBreakdown = getTravelerPrices(
    tripTravelers,
    travelerPricesWithoutPaymentDiscounts,
  );

  return (
    <section
      data-shareable-url={shareableUrl}
      data-testid={`resultPage-resultTrip-${index}-wrapper`}
      key={selectionKey}
    >
      <ResultTrip
        addTrip={addTrip}
        bounds={addLegendNumberToSegmentEvents(bounds)}
        cfarTicketAvailability={
          isCFARTicketAllowedForCarriers(bounds, p('Result.HideCFAR.Carriers')) &&
          p('IbeClient.SearchResult.Flex.Behaviour') ===
            SEARCH_RESULT_FLEX_BEHAVIOR_OPTIONS.CANCEL_FOR_ANY_REASON
            ? getCFARTicketConfig(availableExtraProducts)
            : null
        }
        discountAmount={voucherAmount?.value ?? 0}
        flexibleTicketAvailability={
          isFlexibleTicketAllowedForCarriers(bounds, p('Result.HideFlexTicket.Carriers')) &&
          p('IbeClient.SearchResult.Flex.Behaviour') ===
            SEARCH_RESULT_FLEX_BEHAVIOR_OPTIONS.AIR_REBOOKING
            ? getFlexibleTicketConfig(availableExtraProducts)
            : null
        }
        flightId={flightId}
        hasBookingButton
        isCheckedBaggageIncluded={!isEmpty(includedCheckedBaggage)}
        isMultiStop={isMultiStop}
        isSelectTripLoading={isSelectTripLoading}
        isVI={isVI}
        language={language.code}
        numberOfTravelers={tripTravelers.length}
        onShowAncillaryBundleModal={onShowAncillaryBundleModal}
        paymentMethodPrices={paymentMethodPrices}
        redirectToDBF={redirectToDBF}
        redirectToTravelerDetails={redirectToTravelerDetails}
        selectionKey={selectionKey}
        title={getTripTagsTitle(tripTags, t)}
        travelerPriceBreakdown={travelerPriceBreakdown}
        travelerPrices={getTravelerPrices(tripTravelers, travelerPrices)}
        tripCampaign={tripCampaign}
        tripCharacteristics={tripCharacteristics}
        tripProviderSystems={systems}
        validWithVoucher={
          tripCharacteristics.includes(VALID_WITH_VOUCHER) &&
          p('Result.SelfServiceRebooking.ValidWithVoucherTag.Enable')
        }
      />
      <div data-testid="debug-trip-placeholder" data-tripid={tripId} />
    </section>
  );
};

FlightResult.propTypes = {
  addTrip: PropTypes.func,
  index: PropTypes.number,
  isMultiStop: PropTypes.bool,
  isSelectTripLoading: PropTypes.bool,
  onShowAncillaryBundleModal: PropTypes.func,
  redirectToDBF: PropTypes.func,
  redirectToTravelerDetails: PropTypes.func,
  trip: Trip,
  tripCampaign: TripCampaign,
};

export default FlightResult;
